import { dataKeySummaryState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import type { DataKeyInfo, DataKeySummaryEntry } from '@/util/dataKeyUtils';
import type { AppFlowParams } from '@rock/types';
import { useReactFlow } from '@xyflow/react';
import { type FC, useCallback, useMemo } from 'react';

const DataKeysSummaryPanel: FC<{ params: AppFlowParams }> = ({ params }) => {
    const summary = useAsyncAtomValue(dataKeySummaryState(params));
    const entries = useMemo(
        () => (summary ? [...summary.entries()].sort(([a], [b]) => a.localeCompare(b)) : []),
        [summary],
    );

    if (entries.length === 0) {
        return null;
    }

    return (
        <div className="flex flex-1 flex-col gap-2">
            <h4 className="text-md text-center">Data Keys</h4>
            <div className="overflow-x-auto mx-3">
                <table className="text-xs">
                    <thead>
                        <tr className="text-left text-muted-foreground">
                            <th className="pb-1 font-medium">Key</th>
                            <th className="pb-1 font-medium">Created</th>
                            <th className="pb-1 font-medium">Usages</th>
                        </tr>
                    </thead>
                    <tbody>
                        {entries.map(([key, entry]) => (
                            <DataKeyRow key={key} dataKey={key} entry={entry} />
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const DataKeyRow: FC<{ dataKey: string; entry: DataKeySummaryEntry }> = ({ dataKey, entry }) => {
    const isUnproduced = entry.producers.length === 0 && entry.consumers.length > 0;

    return (
        <tr className={isUnproduced ? 'text-amber-600' : ''}>
            <td className="font-mono font-semibold py-1 pr-2 align-top">{dataKey}</td>
            <td className="py-1 pr-2 align-top">
                <NodeLinkList items={entry.producers} fallback="-" />
            </td>
            <td className="py-1 align-top">
                <NodeLinkList items={entry.consumers} fallback="-" />
            </td>
        </tr>
    );
};

const NodeLinkList: FC<{ items: DataKeyInfo[]; fallback: string }> = ({ items, fallback }) => {
    if (items.length === 0) {
        return <span className="text-muted-foreground">{fallback}</span>;
    }
    return (
        <div className="flex flex-col">
            {items.map((item) => (
                <NodeLink key={`${item.nodeId}-${item.nodeType}`} nodeId={item.nodeId} />
            ))}
        </div>
    );
};

const NodeLink: FC<{ nodeId: string }> = ({ nodeId }) => {
    const { fitView } = useReactFlow();

    const handleClick = useCallback(() => {
        fitView({ nodes: [{ id: nodeId }], duration: 800, padding: 1.5 });
    }, [fitView, nodeId]);

    return (
        <button
            type="button"
            onClick={handleClick}
            className="text-xs font-mono text-sky-600 hover:underline cursor-pointer text-left"
        >
            {nodeId}
        </button>
    );
};

export default DataKeysSummaryPanel;
